import type { ReactNode } from 'react'

/**
 * The twenty-five glyphs the design uses, inlined.
 *
 * ── WHY NOT `lucide-react` ──────────────────────────────────────────────────
 *
 * The standing decision, from `AppShell.tsx:223-225`: "The design system
 * vendors a curated subset the same way; installing lucide-react for two glyphs
 * is 1.4 MB of dependency for ~40 bytes of path data." Twenty-five glyphs does
 * not change that arithmetic, and no sprint since has added a dependency for
 * one.
 *
 * ── WHY NOT THE CANVAS'S `<symbol>` SPRITE ──────────────────────────────────
 *
 * The reference draws these as one `<svg><defs><symbol id="ic-…">` block plus
 * `<use href="#ic-home">` at every call site (canvas lines 132-147). That is a
 * document-level singleton to keep mounted, ordered before every consumer, and
 * alive across route changes — a second thing that can go missing. A component
 * that renders its own paths has no such coupling and costs a few bytes per
 * mount.
 *
 * ── THE CONTRACT ────────────────────────────────────────────────────────────
 *
 * Two props, and no rest spread. A caller cannot override `aria-hidden`, pass a
 * `title`, or set `width` / `height`:
 *
 *  - **Never named.** Every glyph is decoration beside text that already says
 *    what it means (R24). An icon that contributes to an accessible name
 *    changes that name, and "Today" becomes "sun Today" in a screen reader for
 *    no one's benefit. `aria-hidden="true"`, `focusable="false"` (IE/Edge
 *    legacy, and harmless everywhere else), no `role`, no `<title>`.
 *  - **Never sized here.** Size is the CONSUMER's, because the design sizes
 *    every icon by context and not by a base class (`app.css:192`, `:404`,
 *    `:953`, `:1029`). `.icon` carries the fallback size; `.nav__icon`,
 *    `.btn svg` and the rest override it. A `width` attribute would beat all
 *    of them.
 *
 * Mounted nowhere by this package: the nav, the theme buttons and the sign-out
 * button are SHELL's, the dropzone glyph is RECORD's.
 */

export type IconName =
  | 'home'
  | 'upload'
  | 'record'
  | 'clock'
  | 'target'
  | 'compass'
  | 'user'
  | 'sun'
  | 'moon'
  | 'monitor'
  | 'logout'
  | 'menu'
  | 'device'
  | 'pen'
  | 'chev'
  /** Sprint 1.9.13: the paired session's four, from the design system's icon set (BlockRail, the Ready tone row, the pair marks). */
  | 'eye'
  | 'eyeOff'
  | 'bell'
  | 'link'
  /**
   * Sprint 1.9.11 J2: the journal's six labels, for the Journal view's values
   * and the form's group labels. `moon`, `sun`, `pen` and `link` were already
   * here and are reused rather than re-added.
   */
  | 'coffee'
  | 'dumbbell'
  | 'droplet'
  | 'wine'
  | 'tag'
  | 'activity'
  /** agent-1-web: the assistant's speech bubble (nav entry), from the UX canvas. */
  | 'message'

/**
 * Geometry verbatim from the canvas sprite (lines 133-146).
 *
 * The sprite repeats `fill`, `stroke`, `stroke-width` and the joins on every
 * shape; here they sit once on the root `<svg>` and inherit. `stroke="none"` on
 * the two FILLED dots is load-bearing: in the sprite those circles carry no
 * stroke at all, and an inherited 1.8 would swell `record`'s r 3.5 to about
 * 4.4. Round joins on the root are pixel-identical for the shapes whose sprite
 * entry omits `stroke-linejoin` — none of them has a corner.
 */
const GLYPHS: Record<IconName, ReactNode> = {
  home: <path d="M3 9.5 12 3l9 6.5V20a1 1 0 0 1-1 1h-5v-7H9v7H4a1 1 0 0 1-1-1z" />,
  upload: <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4M17 8l-5-5-5 5M12 3v12" />,
  record: (
    <>
      <circle cx="12" cy="12" r="9" />
      <circle cx="12" cy="12" r="3.5" fill="currentColor" stroke="none" />
    </>
  ),
  clock: (
    <>
      <circle cx="12" cy="12" r="9" />
      <path d="M12 7v5l3 2" />
    </>
  ),
  target: (
    <>
      <circle cx="12" cy="12" r="9" />
      <circle cx="12" cy="12" r="5" />
      <circle cx="12" cy="12" r="1.2" fill="currentColor" stroke="none" />
    </>
  ),
  compass: (
    <>
      <circle cx="12" cy="12" r="9" />
      <path d="m15.6 8.4-2.2 5-5 2.2 2.2-5 5-2.2Z" />
    </>
  ),
  user: (
    <>
      <circle cx="12" cy="8" r="4" />
      <path d="M5 21a7 7 0 0 1 14 0" />
    </>
  ),
  sun: (
    <>
      <circle cx="12" cy="12" r="4" />
      <path d="M12 2v2M12 20v2M4.9 4.9l1.4 1.4M17.7 17.7l1.4 1.4M2 12h2M20 12h2M4.9 19.1l1.4-1.4M17.7 6.3l1.4-1.4" />
    </>
  ),
  moon: <path d="M12 3a6.4 6.4 0 0 0 9 9 9 9 0 1 1-9-9Z" />,
  monitor: (
    <>
      <rect x="2" y="3" width="20" height="14" rx="2" />
      <path d="M8 21h8M12 17v4" />
    </>
  ),
  logout: <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4M16 17l5-5-5-5M21 12H9" />,
  menu: <path d="M4 6h16M4 12h16M4 18h16" />,
  device: (
    <>
      <rect x="5" y="2" width="14" height="20" rx="2" />
      <path d="M11 18h2" />
    </>
  ),
  pen: <path d="M12 20h9M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4Z" />,
  chev: <path d="m6 9 6 6 6-6" />,
  // The design system's own paths (brainspot-design-system `_ds_bundle.js`,
  // icons added 2026-10-02 for the paired-session concept), on the same 24-grid
  // at 1.8 stroke as everything above.
  eye: (
    <>
      <path d="M2.062 12.348a1 1 0 0 1 0-.696 10.75 10.75 0 0 1 19.876 0 1 1 0 0 1 0 .696 10.75 10.75 0 0 1-19.876 0" />
      <circle cx="12" cy="12" r="3" />
    </>
  ),
  eyeOff: (
    <path d="M10.733 5.076a10.744 10.744 0 0 1 11.205 6.575 1 1 0 0 1 0 .696 10.747 10.747 0 0 1-1.444 2.49M14.084 14.158a3 3 0 0 1-4.242-4.242M17.479 17.499a10.75 10.75 0 0 1-15.417-5.151 1 1 0 0 1 0-.696 10.75 10.75 0 0 1 4.446-5.143M2 2l20 20" />
  ),
  bell: (
    <path d="M10.3 21a1.9 1.9 0 0 0 3.4 0M3.3 18h17.4a1 1 0 0 0 .7-1.7c-1-1-1.4-2-1.4-3.3V11a8 8 0 0 0-16 0v2c0 1.3-.4 2.3-1.4 3.3a1 1 0 0 0 .7 1.7Z" />
  ),
  link: (
    <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
  ),
  /*
   * Sprint 1.9.11 J2. The `d` strings are VERBATIM from the design system's
   * `ui_kits/brainspot-app/icons.jsx` (lines 8, 14, 17, 28, 29, 30) -- copied
   * rather than retraced, so the glyph on the screen is the glyph the concept
   * was drawn with. Same 24-grid, same 1.8 stroke, same `currentColor` as
   * everything above.
   *
   * `tag`'s second subpath IS the dot, written as a pair of half-circle arcs
   * because the DS set is one `d` per name. It inherits the root's stroke and
   * no fill, so it draws as a ~1px ring rather than Lucide's filled point --
   * the DS's own rendering, kept rather than "fixed", because the concept
   * screenshots are the fidelity reference.
   */
  coffee: <path d="M17 8h1a4 4 0 1 1 0 8h-1 M3 8h14v9a4 4 0 0 1-4 4H7a4 4 0 0 1-4-4Z M6 2v2 M10 2v2 M14 2v2" />,
  dumbbell: (
    <path d="M14.4 14.4 9.6 9.6 M18.7 21l1.3-1.3a1 1 0 0 0 0-1.4L18.7 17 M3.3 3 2 4.3a1 1 0 0 0 0 1.4L3.3 7 M20.7 5.3 18.7 3.3 M5.3 20.7 3.3 18.7 M5 11l6 6 M11 5l6 6 M21.7 12.3l-1.4 1.4 M2.3 11.7 3.7 10.3" />
  ),
  droplet: (
    <path d="M12 22a7 7 0 0 0 7-7c0-2-1-3.9-3-5.5s-3.5-4-4-6.5c-.5 2.5-2 4.9-4 6.5C6 11.1 5 13 5 15a7 7 0 0 0 7 7z" />
  ),
  wine: <path d="M8 22h8 M7 10h10 M12 15v7 M12 15a5 5 0 0 0 5-5c0-2-.5-4-2-8H9c-1.5 4-2 6-2 8a5 5 0 0 0 5 5Z" />,
  tag: (
    <path d="M12.586 2.586A2 2 0 0 0 11.172 2H4a2 2 0 0 0-2 2v7.172a2 2 0 0 0 .586 1.414l8.704 8.704a2.426 2.426 0 0 0 3.42 0l6.58-6.58a2.426 2.426 0 0 0 0-3.42z M7.5 7.5m-.5 0a.5.5 0 1 0 1 0a.5.5 0 1 0-1 0" />
  ),
  activity: <path d="M22 12h-4l-3 9L9 3l-3 9H2" />,
  message: <path d="M21 12a8 8 0 0 1-11.6 7.2L4 21l1.8-5.4A8 8 0 1 1 21 12z" />,
}

export function Icon({ name, className }: { name: IconName; className?: string }) {
  return (
    <svg
      className={['icon', className].filter(Boolean).join(' ')}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      {GLYPHS[name]}
    </svg>
  )
}
